import { Injectable, Optional } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';

import { Store } from '@ngrx/store';

import { EMPTY, Observable, mergeMap, switchMap, withLatestFrom, combineLatest, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { UtilsService } from '@common/modules/shared/services/utils.service';

import { VIRoutingMap } from '../../app/routing/routes';
import { LiveStoreService } from '../../edge/live/services/live-edge-store.service';
import { IState } from '../reducers';
import * as actions from '../actions';
import * as fromCore from '../selectors';
import * as fromRoute from '../reducers/router';
import { IExtensionDetails } from '../interfaces';

import IConnectedClusterExtension = Microsoft.VideoIndexer.Contracts.IConnectedClusterExtension;

@Injectable({
  providedIn: 'root'
})
export class EdgeExtensionsStoreService {
  constructor(
    private readonly store: Store<IState>,
    private utilsService: UtilsService,
    @Optional() private liveStoreService: LiveStoreService,
    private router: Router
  ) {}

  public get extensionDetails$(): Observable<IExtensionDetails> {
    return this.isLiveModuleLoaded$.pipe(
      switchMap(isLiveModuleLoaded => {
        if (isLiveModuleLoaded) {
          return this.liveStoreService.extensionDetails$;
        } else {
          return this.store.select(fromCore.getExtensionDetailsDialogData);
        }
      })
    );
  }

  public get extensionsDetails$(): Observable<IExtensionDetails[]> {
    return this.store.select(fromCore.getAllExtensionDetails);
  }

  public get isEdgeExtensionLiveEnabled$(): Observable<boolean> {
    return this.store.select(fromCore.isLiveStreamEnabled);
  }

  public get isDefaultRoute$(): Observable<boolean> {
    return combineLatest([
      this.isLiveModuleLoaded$,
      this.store.select(fromCore.hasSelectedEdgeExtension),
      this.store.select(fromRoute.getRouterState),
      this.isEdgeExtensionLiveEnabled$
    ]).pipe(
      filter(([, , routerState]) => !!routerState?.state?.data),
      map(([isLiveModuleLoaded, hasSelectedEdgeExtension, routerState, isEdgeExtensionLiveEnabled]) => {
        const routeName = routerState.state.data.name;
        const isEdgeExtensionsRoute = VIRoutingMap.edgeExtensions.name === routeName;

        if (isLiveModuleLoaded && hasSelectedEdgeExtension && isEdgeExtensionLiveEnabled) {
          return of(isEdgeExtensionsRoute);
        } else {
          return this.store.select(fromRoute.isGalleryRoute).pipe(
            take(1),
            map(isGalleryRoute => isGalleryRoute || isEdgeExtensionsRoute)
          );
        }
      }),
      mergeMap(observable => observable)
    );
  }

  public get isExtensionsSubMenuOpened$(): Observable<boolean> {
    return combineLatest([this.isDefaultRoute$, this.hasEdgeExtensions$]).pipe(
      switchMap(([isDefaultRoute, hasEdgeExtensions]) => {
        return [isDefaultRoute && hasEdgeExtensions];
      })
    );
  }

  public get isLiveTextualSummaryEnabled$(): Observable<boolean> {
    return this.store.select(fromCore.isLiveTextualSummaryEnabled);
  }

  public get isAgentsEnabled$(): Observable<boolean> {
    return this.store.select(fromCore.isAgentsEnabled);
  }

  public get edgeExtensionsMetadataLoaded$(): Observable<boolean> {
    return this.store.select(fromCore.isEdgeExtensionsMetadataLoaded);
  }

  public get accountEdgeExtensions$(): Observable<IConnectedClusterExtension[]> {
    return this.store.select(fromCore.selectedAccountEdgeExtensions);
  }

  public get accountEdgeExtensionUrl$(): Observable<string> {
    return this.store.select(fromCore.selectedEdgeExtensionEndpointUrl);
  }

  public get selectedEdgeExtension$(): Observable<IConnectedClusterExtension> {
    return this.store.select(fromCore.selectedEdgeExtension);
  }

  public get selectedEdgeExtensionId$(): Observable<string> {
    return this.store.select(fromCore.selectedEdgeExtensionId);
  }

  public get selectedEdgeExtensionUrl$() {
    return this.store.select(fromCore.selectedEdgeExtension).pipe(
      withLatestFrom(this.store.select(fromCore.selectedAccountContract)),
      mergeMap(([extension, account]) => {
        if (!extension || !account) {
          return EMPTY;
        }

        return [this.utilsService.azurePortalResourceLink(account.tenantId, extension.id)];
      })
    );
  }

  public get selectedEdgeExtensionClusterUrl$() {
    return this.store.select(fromCore.selectedEdgeExtension).pipe(
      withLatestFrom(this.store.select(fromCore.selectedAccountContract)),
      mergeMap(([extension, account]) => {
        if (!extension || !account) {
          return EMPTY;
        }

        const clusterResourceId = this.utilsService.getParentResourceId(extension.id);

        return [this.utilsService.azurePortalResourceLink(account.tenantId, clusterResourceId)];
      })
    );
  }

  public get selectEdgeExtensionsLoaded$(): Observable<boolean> {
    return this.store.select(fromCore.selectEdgeExtensionsLoaded);
  }

  public get selectEdgeExtensions$(): Observable<Microsoft.VideoIndexer.Contracts.IConnectedClusterExtension[]> {
    return this.store.select(fromCore.selectEdgeExtensions);
  }

  public get hasSelectedEdgeExtension$(): Observable<boolean> {
    return combineLatest([this.store.select(fromCore.hasSelectedEdgeExtension), this.store.select(fromCore.isNavigatingToEdgeExtension)]).pipe(
      filter(([_, isNavigatingToEdgeExtension]) => !isNavigatingToEdgeExtension),
      map(([hasSelectedEdgeExtension]) => hasSelectedEdgeExtension)
    );
  }

  public get hasEdgeExtensions$(): Observable<boolean> {
    return this.store.select(fromCore.hasEdgeExtensions);
  }

  public get selectedEdgeExtensionUniqueId$(): Observable<string> {
    return this.store.select(fromCore.selectedEdgeExtensionUniqueId);
  }

  public get selectedEdgeExtensionName$(): Observable<string> {
    return combineLatest([this.store.select(fromCore.selectedEdgeExtensionName), this.store.select(fromCore.isNavigatingToEdgeExtension)]).pipe(
      filter(([_, isNavigatingToEdgeExtension]) => !isNavigatingToEdgeExtension),
      map(([selectedEdgeExtensionName]) => selectedEdgeExtensionName)
    );
  }

  public get selectedEdgeExtensionIotHubResourceId$(): Observable<string> {
    return this.store.select(fromCore.selectedEdgeExtensionIotHubResourceId);
  }

  public get isSelectedEdgeExtensionError$(): Observable<boolean> {
    return this.store.select(fromCore.isSelectedEdgeExtensionError);
  }

  public get isLiveStreamEnabled$(): Observable<boolean> {
    return this.store.select(fromCore.isLiveStreamEnabled);
  }

  public get isHandshakeInProgress$(): Observable<boolean> {
    return this.store.select(fromCore.selectedEdgeExtensionHandshakeInProgress);
  }

  public get handshakeDone$(): Observable<boolean> {
    return this.store.select(fromCore.selectedEdgeExtensionHandshakeDone);
  }

  public get isLiveModuleLoaded$(): Observable<boolean> {
    return this.store.select(fromCore.isLiveModuleLoaded);
  }

  public get isNavigationEnd$(): Observable<boolean> {
    return this.router.events.pipe(map(event => event instanceof NavigationEnd));
  }

  public clearSelectedEdgeExtension() {
    this.store.dispatch(actions.clearSelectedEdgeExtension());
  }

  public clearEdgeExtensions() {
    this.store.dispatch(actions.clearSelectedEdgeExtension());
    this.store.dispatch(actions.clearEdgeExtensions());
  }

  public selectEdgeExtension(id: string) {
    this.store.dispatch(actions.selectEdgeExtension({ id }));
  }

  public selectEdgeExtensionClicked(id: string) {
    this.store.dispatch(actions.selectEdgeExtensionClicked({ id }));
  }

  public selectEdgeExtensionByPrincipalId(principalId: string) {
    this.store.dispatch(actions.selectEdgeExtensionByPrincipalId({ principalId }));
  }

  public uploadFile(file: File, name?: string) {
    this.store.dispatch(actions.uploadFile({ file, name }));
  }

  public openExtensionDetailsDialog() {
    this.store.dispatch(actions.openExtensionDetailsDialog());
  }

  public liveModuleLoaded() {
    this.store.dispatch(actions.liveModuleLoaded());
  }
}
